import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { useGetBingoEventQuery } from '@/api/queries/bingo';
import { AppRoute } from '@/common/app/routes';
import { type ContractDataType } from '@/common/types/contract';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

interface BingoNoBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SourceItem {
  icon: string;
  title: string;
  description: string;
  route?: string;
  action?: () => void;
  extra?: React.ReactNode;
}

function getSecondsUntilMidnightUTC(): number {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setUTCDate(midnight.getUTCDate() + 1);
  midnight.setUTCHours(0, 0, 0, 0);

  return Math.max(0, Math.floor((midnight.getTime() - now.getTime()) / 1000));
}

function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const BingoNoBalanceModal: FC<BingoNoBalanceModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalContext);
  const { data: event } = useGetBingoEventQuery();
  const firstDefaultContract = useAppSelector((state) =>
    state.contract.list?.find((c) => c.type === 'Default'),
  ) as ContractDataType | undefined;

  const dailyBalls = event?.dailyContractBalls ?? 0;
  const dailyLimit = event?.dailyContractBallsLimit ?? 0;
  const isLimitReached = dailyBalls >= dailyLimit;

  const [countdown, setCountdown] = useState(getSecondsUntilMidnightUTC);

  useEffect(() => {
    if (!isOpen || !isLimitReached) return;
    setCountdown(getSecondsUntilMidnightUTC());
    const interval = setInterval(() => {
      setCountdown(getSecondsUntilMidnightUTC());
    }, 1000);

    return (): void => clearInterval(interval);
  }, [isOpen, isLimitReached]);

  const sources: SourceItem[] = [
    {
      icon: '📋',
      title: t('Complete regular contracts'),
      description: isLimitReached
        ? t('Daily limit reached')
        : `${dailyBalls / 5}/${dailyLimit / 5} ` +
          t('daily limit. For each contract you get 5 balls'),
      action:
        isLimitReached || !firstDefaultContract
          ? undefined
          : (): void => {
              navigate(AppRoute.MAIN);
              setTimeout(() => {
                openModal({
                  renderKind: 'takeContract',
                  props: { contractData: firstDefaultContract },
                });
              }, 300);
            },
      extra: isLimitReached ? (
        <StringXSmall className="!text-[#FF6B6B] mt-0.5">
          {t('Resets in')} {formatTimer(countdown)}
        </StringXSmall>
      ) : null,
    },
    {
      icon: '🔴',
      title: t('Buy bingo balls'),
      description: t('Purchase balls right now'),
      action: (): void => {
        openModal({
          renderKind: 'buyBingoBallsModal',
          props: { source: 'no_balance_modal' },
        });
      },
    },
    // {
    //   icon: '🛒',
    //   title: t('Shop'),
    //   description: t('Buy balls with special offers'),
    //   route: AppRoute.SHOP,
    // },
  ];

  const handleItem = (item: SourceItem): void => {
    if (!item.route && !item.action) return;
    onClose();

    if (item.route) navigate(item.route);
    item.action?.();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={css.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className={css.sheet}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className={css.handle} />

            <div className={css.header}>
              <div className={css.ballIcon} />
              <div className={css.headerText}>
                <H2 weight="bold" className={css.headerTitle}>
                  {t('Not enough balls')}
                </H2>
                <StringXSmall className={css.headerSub}>
                  {t('Choose a way to get more')}
                </StringXSmall>
              </div>
            </div>

            <div className={css.list}>
              {sources.map((item, idx) => {
                const disabled = !item.route && !item.action;

                return (
                  <motion.button
                    key={idx}
                    className={css.item}
                    onClick={() => handleItem(item)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: disabled ? 0.5 : 1, x: 0 }}
                    transition={{ delay: 0.08 + idx * 0.05, duration: 0.25 }}
                    whileTap={disabled ? undefined : { scale: 0.97 }}
                    disabled={disabled}
                  >
                    <span className={css.itemIcon}>{item.icon}</span>
                    <div className={css.itemText}>
                      <StringMain weight="bold" className={css.itemTitle}>
                        {item.title}
                      </StringMain>
                      <StringXSmall className={css.itemDesc}>
                        {item.description}
                      </StringXSmall>
                      {item.extra}
                    </div>
                    {!disabled && (
                      <svg
                        className={css.arrow}
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M6 4l4 4-4 4"
                          stroke="rgba(255,255,255,0.35)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </motion.button>
                );
              })}
            </div>

            <button className={css.cancel} onClick={onClose}>
              <StringMain className={css.cancelText}>{t('Cancel')}</StringMain>
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export { BingoNoBalanceModal };
